<template>
  <div class="time-picker-modal-container fade-in-load">
    <div>
      <div class="time-picker-modal-header">
        <span
          @click="handleStepChange(0)"
          @keydown.enter="handleStepChange(0)"
          @keydown.space="handleStepChange(0)"
          role="button"
          tabindex="0"
          class="time-picker-header"
          :class="{ active: step === 0 }">
          {{ hourString }} &nbsp;&nbsp;
        </span>
        <span
          @click="handleStepChange(1)"
          @keydown.enter="handleStepChange(1)"
          @keydown.space="handleStepChange(1)"
          role="button"
          tabindex="0"
          class="time-picker-header"
          :class="{ active: step === 1 }">
          {{ minuteString }} &nbsp;&nbsp;
        </span>
        <span
          @click="handleStepChange(2)"
          @keydown.enter="handleStepChange(2)"
          @keydown.space="handleStepChange(2)"
          role="button"
          tabindex="0"
          class="time-picker-header"
          :class="{ active: step === 2 }">
          {{ amString }}
        </span>
      </div>
    </div>
    <div class="picker-container">
      <time-picker-generator
        :handle-time-pointer-click="handleTimePointerClick"
        :clear-modal="clearModal"
        :handle-time-change="handleTimeChange"
        :type="timeType"
        :hour="hour"
        :minute="minute"
        :step="step" />
    </div>
  </div>
</template>

<script>
import { MINUTES, HOURS } from 'assets/js/widgets/clockpicker';
import TimePickerGenerator from 'components/Widgets/ClockPicker/TimePickerGenerator';

const STEP_TYPE_HOUR = 0;
const STEP_TYPE_MIN = 1;
const STEP_TYPE_AMPM = 2;

export default {
  props: {
    clearModal: {
      type: Function,
      default: () => {},
    },
    initStep: {
      type: Number,
      default: 0,
    },
    initHour: {
      type: [Number, String],
      default: '00',
    },
    initMinute: {
      type: [Number, String],
      default: '00',
    },
    handleHourChange: {
      type: Function,
      default: () => {},
    },
    handleMinuteChange: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      step: this.initStep,
      pointerRotate: () => {
        this.resetHourDegree();
      },
      hour: this.initHour,
      minute: this.initMinute,
    };
  },
  computed: {
    timeType() {
      return this.step === 0 ? 'hour' : 'minute';
    },
    amString() {
      return this.hour < 12 ? 'AM' : 'PM';
    },
    hourString() {
      if (this.hour === 0) {
        return '12';
      }
      if (this.hour < 13) {
        return String(this.hour);
      }
      return String(this.hour - 12);
    },
    minuteString() {
      return this.minute < 10 ? `0${this.minute}` : String(this.minute);
    },
  },
  components: {
    TimePickerGenerator,
  },
  methods: {
    handleStepChange(s) {
      if (s !== this.step) {
        this.step = s;
        if (s === STEP_TYPE_AMPM) {
          return;
        }
        if (this.step === STEP_TYPE_HOUR) {
          this.pointerRotate = this.resetHourDegree();
        }
        if (this.step === STEP_TYPE_MIN) {
          this.pointerRotate = this.resetMinuteDegree();
        }
      }
    },
    handleTimePointerClick(time, rotate) {
      this.pointerRotate = rotate;
      this.handleTimeChange(time);
    },
    handleTimeChange(timeArg) {
      const time = timeArg;
      if (this.step === STEP_TYPE_HOUR) {
        this.hour = time;
        if (this.handleHourChange) this.handleHourChange(time);
      }
      if (this.step === STEP_TYPE_MIN) {
        this.minute = time;
        if (this.handleMinuteChange) this.handleMinuteChange(time);
      }
      if (this.step === STEP_TYPE_AMPM) {
        this.hour = time;
        if (this.handleHourChange) this.handleHourChange(time);
      }
      this.step += 1;

      // Close modal and reset if we set AM/PM.
      if (this.step > 2) {
        this.step = 0;
        this.clearModal();
        return;
      }

      this.handleStepChange(this.step);
    },
    resetHourDegree() {
      const h = this.hour;
      let pointerR = 0;
      HOURS.forEach((hour, index) => {
        if (h === index + 1) {
          pointerR =
            index < 12
              ? (360 * (index + 1)) / 12
              : (360 * (index + 1 - 12)) / 12;
        }
      });
      return pointerR;
    },
    resetMinuteDegree() {
      const m = this.minute;
      let pointerR = 0;
      MINUTES.forEach((minute, index) => {
        if (m === index) {
          pointerR = (360 * index) / 60;
        }
      });
      return pointerR;
    },
  },
};
</script>

<style lang="scss">
.fade-in-load {
  animation: fade-in-load 0.3s;
}

@keyframes fade-in-load {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.time-picker-modal-container {
  width: 300px;
  isolation: isolate;
  position: absolute;
  z-index: 999;
  opacity: 1;
  padding: 25px;
  transition: all 400ms cubic-bezier(0.165, 0.84, 0.44, 1);
  background-color: #fff;
  border-radius: 2px;
  top: 100%;
  left: 0;
  transform: translate(-108px, 20px);
  // box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.12),
  //  0 0 4px 0 rgba(0, 0, 0, 0.08);
  border: 1px solid #d1dbe5;
  box-shadow: 0 2px 6px #ccc;
}
.time-picker-modal-container:hover {
  box-shadow:
    0 8px 8px 0 rgba(0, 0, 0, 0.12),
    0 0 8px 0 rgba(0, 0, 0, 0.08);
}

@media only screen and (max-width: 840px) {
  .time-picker-modal-container {
    width: 300px;
    position: fixed;
    opacity: 1;
    z-index: 2;
    top: 50%;
    left: 50%;
    margin-top: -18em;
    margin-left: -10.5em;
    padding: 25px;
    transform: translate(0px, 20px);
    transition: all 400ms cubic-bezier(0.165, 0.84, 0.44, 1);
    background-color: #fff;
    border-radius: 2px;
    border: 1px solid #d1dbe5;
    box-shadow: 0 2px 6px #ccc;
  }
}

.time-picker-modal-header {
  height: 80px;
  line-height: 40px;
  text-align: center;
  font-size: 2.5em;
  position: relative;
}

.time-picker-modal-header .time-picker-header {
  cursor: pointer;
}
.time-picker-modal-header .time-picker-header.active {
  color: #20a0ff !important;
  cursor: default;
}
.time-picker-modal-header .time-picker-header:hover {
  color: #20a0ff !important;
}

.picker-container {
  width: 250px;
  height: 250px;
  border-radius: 50%;
  background-color: #e5e5e5;
  position: relative;
}
.picker-center {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #20a0ff !important;
  transform: translate(-50%, -50%);
}
.picker-pointer {
  position: absolute;
  width: 4px;
  height: 110px;
  left: 50%;
  top: 20px;
  background-color: #20a0ff !important;
  transform-origin: center bottom;
}

.picker-pointer.animation {
  transition: all 400ms cubic-bezier(0.165, 0.84, 0.44, 1);
}
</style>
